import { createFileRoute, Link } from "@tanstack/react-router";
import { seo } from "@/lib/seo";
import {
  CheckList,
  ClosingCta,
  MarketingPage,
  MarketingSection,
  StartFreeButton,
} from "@/components/marketing/marketing-kit";

export const Route = createFileRoute("/test-tracker")({
  head: () =>
    seo({
      title: "JEE Test Tracker — Plan Tests & Track PCM Scores | The Quantum Mind",
      description:
        "Free JEE test tracker: schedule chapter-wise revision before every test, log Physics, Chemistry and Maths marks, and track averages, improvement and weak subjects.",
      path: "/test-tracker",
    }),
  component: TestTrackerLanding,
});

function TestTrackerLanding() {
  return (
    <MarketingPage>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        JEE test tracker
      </p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl">
        Plan every test and track every score
      </h1>
      <p className="mt-4 text-[15px] leading-relaxed text-muted-foreground">
        Add a test with its date and syllabus, and The Quantum Mind builds a day-by-day revision and
        practice schedule up to test day. On the day itself you enter subject-wise marks, and the
        app keeps your score history, averages and improvement trend.
      </p>
      <div className="mt-6 flex flex-wrap gap-3">
        <StartFreeButton label="Create your first test" />
        <Link
          to="/tests"
          className="glass inline-flex items-center justify-center rounded-full border border-hairline px-6 py-3 text-sm font-semibold transition-transform active:scale-95"
        >
          Open the test tracker
        </Link>
      </div>

      <MarketingSection title="What the test tracker does">
        <CheckList
          items={[
            "Unlimited tests, each with its own schedule and progress",
            "Automatic daily revision and practice tasks before the test",
            "Clear status: upcoming, today, completed or missed",
            "Physics, Chemistry and Maths marks validated against the total",
            "Subject-wise percentages and full score history",
            "Averages, highs, lows, improvement trend and streaks",
          ]}
        />
      </MarketingSection>

      <MarketingSection title="Why score tracking matters">
        <p className="text-sm leading-relaxed text-muted-foreground">
          A single mock score tells you very little. A sequence of scores, split by subject, tells
          you whether Physics is actually improving, whether Chemistry is carrying your total, and
          which chapters keep costing you marks. That is the difference between practising and
          preparing.
        </p>
      </MarketingSection>

      <ClosingCta
        title="Track your next test properly"
        body="Create a free account to save your test schedules and scores."
      />
    </MarketingPage>
  );
}
